import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { createApi } from '@Pages/pageRuntime/sdk/api';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkAvailabilityHandle,
  SdkAvailabilityOptions,
  SdkRecordAvailability
} from '@Pages/pageRuntime/sdk/types';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';

import {
  type AvailabilityServerRecord,
  availabilityBackgroundTimes
} from './availabilityBackgroundTimes';
import { schedulingRequestDatetime } from './schedulingRequestDatetime';

type AvailabilityResponse = { records: AvailabilityServerRecord[] };

const NO_RECORDS: SdkRecordAvailability[] = [];

/** Runtime implementation of `Sdk.hooks.useAvailability`. */
export function useAvailability({
  resourceId,
  ids,
  from,
  to,
  enabled = true,
  labels
}: SdkAvailabilityOptions): SdkAvailabilityHandle {
  const fromValue = schedulingRequestDatetime(from);
  const toValue = schedulingRequestDatetime(to);
  const canRead =
    enabled && resourceId.length > 0 && ids.length > 0 && fromValue !== '' && toValue !== '';
  const body = { ids, from: fromValue, to: toValue };
  const query = useQuery<AvailabilityResponse, ReturnType<typeof toApiError>>({
    queryKey: [PAGE_API_QUERY_KEY_HEAD, 'resources', resourceId, 'availability', body],
    enabled: canRead,
    retry: false,
    refetchOnMount: 'always',
    staleTime: 0,
    throwOnError: false,
    queryFn: async ({ signal }) =>
      createApi(axios).post<AvailabilityResponse>(
        `/api/data/resources/${encodeURIComponent(resourceId)}/availability`,
        body,
        { signal }
      )
  });
  // A refetch of the same inputs keeps the last result on screen; new inputs have no data yet.
  const isLoading = canRead && query.data === undefined && (query.isFetching || query.isPaused);
  const result = canRead && !query.isError ? query.data : undefined;
  // Pages usually pass `labels` inline, so the memo follows the resolved words, not the object.
  const busyLabel = labels === false ? null : (labels?.busy ?? 'Busy');
  const closedLabel = labels === false ? null : (labels?.closed ?? 'Closed');
  const records = useMemo(() => {
    if (result === undefined) {
      return NO_RECORDS;
    }

    const words =
      busyLabel === null || closedLabel === null ? false : { busy: busyLabel, closed: closedLabel };

    return result.records.map((record) => ({
      ...record,
      backgroundTimes: availabilityBackgroundTimes(record, words)
    }));
  }, [result, busyLabel, closedLabel]);

  return {
    records,
    isLoading,
    error: canRead ? query.error : null,
    refetch: async () => {
      if (canRead) {
        await query.refetch();
      }
    }
  };
}
